import React, { useMemo, useState, useRef, useEffect } from "react"
import { type CustomTooltipProps } from "ag-grid-react"
import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import { formatNumber } from "~/utils/formatting"
import HeaderTooltip from "./HeaderTooltip"
import { api } from "~/utils/api"
import Highcharts from "highcharts"
import { HighchartsReact } from "highcharts-react-official"
import { useCompany } from "~/hooks/useCompany"
import { capitalize } from "lodash"
import { generateDistinctColors } from "~/utils/colors"

interface ConstraintBreakdownChartProps {
  constraints: Array<{
    constraintId: number
    filter: Record<string, string> | null
    constraintAvgUnitPrice: number
    variantForecast: number
  }>
  weightedAvgUnitPrice: number
  currency: string
  onHoverConstraint: (constraintId: number | null) => void
  hoveredConstraintId: number | null
  scalingFactor?: number
  colors: string[]
}

function formatFilterLabel(filter: Record<string, string> | null): string {
  if (!filter || Object.keys(filter).length === 0) return "All"
  return Object.entries(filter)
    .map(([key, value]) => `${capitalize(key)}: ${value}`)
    .join(", ")
}

const ConstraintBreakdownChart = ({
  constraints,
  weightedAvgUnitPrice,
  currency,
  onHoverConstraint,
  hoveredConstraintId,
  scalingFactor = 1,
  colors,
}: ConstraintBreakdownChartProps) => {
  const chartOptions = useMemo(() => {
    if (constraints.length === 0) return null

    const totalForecast = constraints.reduce((sum, c) => sum + c.variantForecast, 0)

    // build series data for each constraint
    // reverse so first row (highest weight) appears leftmost in the stacked bar
    const series = constraints
      .map((constraint, idx) => {
        const weight = totalForecast > 0 ? constraint.variantForecast / totalForecast : 0
        // Scale the contribution to account for variants that don't match any constraint
        const contribution = weight * constraint.constraintAvgUnitPrice * scalingFactor
        const isHovered = hoveredConstraintId === constraint.constraintId
        const isSomethingHovered = hoveredConstraintId !== null

        return {
          type: "bar" as const,
          name: formatFilterLabel(constraint.filter),
          data: [contribution] as any,
          color: colors[idx],
          opacity: isSomethingHovered ? (isHovered ? 1 : 0.3) : 1,
          stacking: "normal" as const,
          custom: {
            constraintId: constraint.constraintId,
            weight: weight * 100,
            avgPrice: constraint.constraintAvgUnitPrice * scalingFactor,
            forecast: constraint.variantForecast,
          },
          states: {
            hover: {
              brightness: 0.15,
            },
          },
        }
      })
      .reverse()

    return {
      chart: { height: 80, backgroundColor: "transparent" },
      title: { text: undefined },
      xAxis: {
        categories: [""],
        labels: { enabled: false },
      },
      yAxis: {
        title: { text: undefined },
        labels: {
          format: `${currency}{value}`,
          style: {
            fontSize: "10px",
            color: "var(--color-fg-base)",
          },
        },
      },
      legend: { enabled: false },
      plotOptions: {
        bar: {
          stacking: "normal",
          dataLabels: { enabled: false },
          borderWidth: 0,
        },
        series: {
          enableMouseTracking: true,
          cursor: "pointer",
          events: {
            mouseOver: function () {
              const series = this as any
              const constraintId = series?.options?.custom?.constraintId
              if (constraintId !== undefined) {
                onHoverConstraint(constraintId)
              }
            },
            mouseOut: function () {
              onHoverConstraint(null)
            },
          },
        },
      },
      tooltip: {
        enabled: false,
      },
      credits: { enabled: false },
      series,
    } as Highcharts.Options
  }, [
    constraints,
    weightedAvgUnitPrice,
    currency,
    onHoverConstraint,
    hoveredConstraintId,
    scalingFactor,
    colors,
  ])

  if (!chartOptions) {
    return <div className="text-fg-muted paragraph-sm">No budget target data available</div>
  }

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />
}

interface ConstraintAvgPriceTooltipProps extends CustomTooltipProps {
  value?: {
    budgetId: number
    variantIds: string[]
    isAggregated?: boolean
    avgConstraintPrice?: number
  }
}

const ConstraintAvgPriceTooltip = (props: ConstraintAvgPriceTooltipProps) => {
  const { data: company } = useCompany()
  const { budgetId, variantIds = [], isAggregated = false, avgConstraintPrice } = props.value || {}
  const [hoveredConstraintId, setHoveredConstraintId] = useState<number | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const { data: breakdownData, isLoading } = api.budgets.getVariantBudgetBreakdown.useQuery(
    { budgetId: budgetId ?? 0, variantIds },
    { enabled: !!budgetId && variantIds.length > 0 },
  )

  // Prepare sorted constraints for both chart and table
  const sortedConstraints = useMemo(() => {
    if (!breakdownData) return []
    return breakdownData.constraints
      .filter((c) => c.variantForecast > 0)
      .sort((a, b) => b.variantForecast - a.variantForecast)
  }, [breakdownData])

  const constraintColors = useMemo(
    () => generateDistinctColors(sortedConstraints.length),
    [sortedConstraints.length],
  )

  useEffect(() => {
    if (!tooltipRef.current) return

    const tooltip = tooltipRef.current
    const rect = tooltip.getBoundingClientRect()
    const viewportWidth = window.innerWidth

    // check if tooltip goes off the right edge of the screen
    if (rect.right > viewportWidth - 16) {
      const overflow = rect.right - viewportWidth + 16
      tooltip.style.transform = `translateX(-${overflow}px)`
    }
    // check if tooltip goes off the left edge of the screen
    else if (rect.left < 16) {
      const overflow = 16 - rect.left
      tooltip.style.transform = `translateX(${overflow}px)`
    }
  }, [breakdownData])

  if (props.location === "header") return <HeaderTooltip {...props} />
  if (!budgetId || variantIds.length === 0) return null
  if (isLoading) return null
  if (!breakdownData || breakdownData.constraints.length === 0) return null

  const onMouseLeave = () => props.hideTooltipCallback?.()

  const currency = company?.currency || "$"

  // Use provided avgConstraintPrice for aggregated data, otherwise use backend-calculated value
  const displayWeightedAvg = avgConstraintPrice ?? breakdownData.weightedAvgUnitPrice ?? 0

  // Calculate scaling factor to account for variants that don't match any constraint
  // This ensures individual constraint contributions sum to the correct weighted average
  const backendWeightedAvg = breakdownData.weightedAvgUnitPrice ?? 0
  const scalingFactor =
    backendWeightedAvg > 0 && displayWeightedAvg !== backendWeightedAvg
      ? displayWeightedAvg / backendWeightedAvg
      : 1

  return (
    <div
      ref={tooltipRef}
      className="bg-base z-10 w-[450px]"
      style={{
        maxWidth: "min(450px, calc(100vw - 2rem))",
      }}
      onMouseLeave={onMouseLeave}
    >
      <Card className="w-full">
        <CardHeader>
          <CardTitle>
            {isAggregated
              ? `Target row breakdown (${variantIds.length} variants)`
              : "Target row breakdown"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ConstraintBreakdownChart
            constraints={sortedConstraints}
            weightedAvgUnitPrice={displayWeightedAvg}
            currency={currency}
            onHoverConstraint={setHoveredConstraintId}
            hoveredConstraintId={hoveredConstraintId}
            scalingFactor={scalingFactor}
            colors={constraintColors}
          />
          <div className="border-t border-base pt-3">
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col className="w-[55%]" />
                <col className="w-[25%]" />
                <col className="w-[20%]" />
              </colgroup>
              <thead>
                <tr className="text-fg-muted">
                  <th className="text-left font-medium pb-2">Target row</th>
                  <th className="font-medium pb-2">Contribution</th>
                  <th className="font-medium pb-2">Weight</th>
                </tr>
              </thead>
              <tbody>
                {sortedConstraints.map((constraint, idx) => {
                  const totalForecast = breakdownData.totalForecast ?? 0
                  const weightFraction =
                    totalForecast > 0 ? constraint.variantForecast / totalForecast : 0
                  const weightPct = weightFraction * 100
                  const contribution =
                    weightFraction * constraint.constraintAvgUnitPrice * scalingFactor
                  const isHovered = hoveredConstraintId === constraint.constraintId
                  return (
                    <tr
                      key={constraint.constraintId}
                      className={`border-t border-base/50 transition-colors cursor-pointer ${
                        isHovered ? "bg-primary/10" : ""
                      }`}
                      style={isHovered ? { backgroundColor: "rgba(59, 130, 246, 0.1)" } : {}}
                      onMouseEnter={() => setHoveredConstraintId(constraint.constraintId)}
                      onMouseLeave={() => setHoveredConstraintId(null)}
                    >
                      <td className="py-1.5">
                        <div className="flex items-start gap-2">
                          <span
                            className="w-3 h-3 rounded-sm flex-shrink-0 mt-0.5"
                            style={{
                              backgroundColor: constraintColors[idx],
                            }}
                          />
                          <span className="break-words">
                            {formatFilterLabel(constraint.filter)}
                          </span>
                        </div>
                      </td>
                      <td className="py-1.5">
                        {currency} {formatNumber(contribution, 2)}
                      </td>
                      <td className="py-1.5">{formatNumber(weightPct, 1)}%</td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-base font-medium">
                  <td className="py-1.5">Weighted Average</td>
                  <td className="py-1.5">
                    {currency} {formatNumber(displayWeightedAvg, 2)}
                  </td>
                  <td className="py-1.5">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default ConstraintAvgPriceTooltip
